/**
 * Marketplace metadata normalization utilities
 * Normalizes Stripe product metadata into a consistent marketplace model
 */

/**
 * Provider configuration
 */
export const PROVIDERS = {
  besper_finance: {
    id: 'besper_finance',
    displayName: 'Besper Finance',
    logoKey: 'besper_finance',
    vendorType: 'besper'
  },
  besper_products: {
    id: 'besper_products',
    displayName: 'Besper Products',
    logoKey: 'besper_products',
    vendorType: 'besper'
  },
  besper_ai: {
    id: 'besper_ai',
    displayName: 'Besper AI',
    logoKey: 'besper_ai',
    vendorType: 'besper'
  },
  besper_storage: {
    id: 'besper_storage',
    displayName: 'Besper Storage',
    logoKey: 'besper_products',
    vendorType: 'besper'
  }
};

/**
 * Category configuration
 */
export const CATEGORIES = {
  ai_chatbot: {
    id: 'ai_chatbot',
    displayName: 'AI Chatbot',
    icon: '🤖'
  },
  ai_lab: {
    id: 'ai_lab',
    displayName: 'AI Lab',
    icon: '🧪'
  },
  erp: {
    id: 'erp',
    displayName: 'ERP System',
    icon: '📊'
  },
  crm: {
    id: 'crm',
    displayName: 'CRM',
    icon: '👥'
  },
  productivity: {
    id: 'productivity',
    displayName: 'Productivity',
    icon: '⚡'
  },
  analytics: {
    id: 'analytics',
    displayName: 'Analytics',
    icon: '📈'
  },
  integration: {
    id: 'integration',
    displayName: 'Integration',
    icon: '🔌'
  },
  developer_tools: {
    id: 'developer_tools',
    displayName: 'Developer Tools',
    icon: '🛠️'
  },
  other: {
    id: 'other',
    displayName: 'Other',
    icon: '📦'
  },
  dataset_storage: {
    id: 'dataset_storage',
    displayName: 'Dataset',
    icon: '💾'
  },
  security: {
    id: 'security',
    displayName: 'Security',
    icon: '🔐'
  },
  platform: {
    id: 'platform',
    displayName: 'Platform',
    icon: '🧩'
  },
  operations: {
    id: 'operations',
    displayName: 'Operations',
    icon: '📋'
  }
};

/**
 * Normalize Stripe product metadata into marketplace model
 * @param {Object} product - Stripe product object
 * @returns {Object} Normalized marketplace product
 */
export function normalizeMarketplaceProduct(product) {
  const metadata = product.metadata || {};
  
  // Extract and normalize metadata
  const vendorType = metadata.vendor_type || 'besper';
  const providerId = metadata.provider || null;
  const rawProviderDisplayName = metadata.provider_display_name || 
    (providerId && PROVIDERS[providerId] ? PROVIDERS[providerId].displayName : null);
  // Remove star symbols from provider display name
  const providerDisplayName = rawProviderDisplayName ? rawProviderDisplayName.replace(/★/g, '').trim() : null;
  
  const categoryId = metadata.category || 'other';
  const category = CATEGORIES[categoryId] || CATEGORIES.other;
  
  // Parse boolean strings
  const costPool = metadata.cost_pool !== 'false';
  const trialAvailable = metadata.trial_available === 'true';
  const marketplaceFeatured = metadata.marketplace_featured === 'true';
  
  // Parse numeric strings
  const trialDays = trialAvailable && metadata.trial_days ? parseInt(metadata.trial_days, 10) : null;
  const marketplacePriority = metadata.marketplace_priority ? parseInt(metadata.marketplace_priority, 10) : null;
  
  // Parse comma-separated strings or arrays
  const features = Array.isArray(metadata.features)
    ? metadata.features.map(f => String(f).trim()).filter(Boolean)
    : (metadata.features 
        ? metadata.features.split(',').map(f => f.trim()).filter(Boolean) 
        : []);
  
  const tags = Array.isArray(metadata.tags)
    ? metadata.tags.map(t => String(t).trim().toLowerCase()).filter(Boolean)
    : (metadata.tags 
        ? metadata.tags.split(',').map(t => t.trim().toLowerCase()).filter(Boolean) 
        : []);
  
  // Get product logo (prefer Stripe images, fallback to metadata)
  const productLogoUrl = product.images && product.images.length > 0 
    ? product.images[0] 
    : metadata.product_logo_url || null;
  
  // Provider logo (tenant marketplace tab upload) - for third-party product cards top-right
  const providerLogoUrl = metadata.provider_logo_url || null;
  
  // Get provider info
  const provider = providerId && PROVIDERS[providerId] 
    ? { ...PROVIDERS[providerId], logo_url: providerLogoUrl || PROVIDERS[providerId].logo_url }
    : (providerLogoUrl ? { id: providerId, displayName: providerDisplayName, logo_url: providerLogoUrl } : null);

  const destRaw = String(metadata.default_dataset_destination || '').toLowerCase();
  const isFileLikeDataset =
    metadata.resource_type === 'dataset' &&
    (destRaw === 'file' || destRaw === 'blob' || destRaw === '');
  const marketplaceDisplayName = isFileLikeDataset
    ? 'Dataset'
    : (product.name || '');
  const marketplaceShortDescription = isFileLikeDataset
    ? 'Dataset file storage with a fixed GiB/month capacity cap.'
    : (metadata.short_description || product.description || '');

  return {
    // Original product data
    ...product,
    name: marketplaceDisplayName,

    // Normalized marketplace metadata
    marketplace: {
      // Vendor/Provider
      vendorType,
      providerId,
      providerDisplayName,
      provider,
      providerLogoUrl,
      
      // Category
      categoryId,
      category,
      subcategory: metadata.subcategory || null,
      tags,
      
      // Display
      shortDescription: marketplaceShortDescription,
      features,
      productLogoUrl,
      
      // Trials
      trialAvailable,
      trialDays,
      
      // Merchandising
      marketplaceFeatured,
      marketplacePriority: marketplacePriority !== null ? marketplacePriority : 999, // Default to low priority
      
      // Billing
      costPool,
      
      // Dataset backend destination (e.g. blob, postgres, clickhouse, tigerbeetle, victoriametrics)
      defaultDatasetDestination: metadata.default_dataset_destination || null,

      // Status (already normalized in useResourceData)
      status: metadata.status || 'public'
    }
  };
}

/**
 * Validate marketplace product metadata
 * @param {Object} product - Stripe product object
 * @returns {Object} Validation result with isValid and errors
 */
export function validateMarketplaceProduct(product) {
  const metadata = product.metadata || {};
  const errors = [];
  
  // Required fields for Besper products
  if (metadata.vendor_type === 'besper' || !metadata.vendor_type) {
    if (!metadata.provider) {
      errors.push(`Missing required metadata: provider`);
    }
    if (!metadata.category) {
      errors.push(`Missing required metadata: category`);
    }
  }
  
  // Validate provider exists
  if (metadata.provider && !PROVIDERS[metadata.provider]) {
    errors.push(`Invalid provider: ${metadata.provider}`);
  }
  
  // Validate category exists
  if (metadata.category && !CATEGORIES[metadata.category]) {
    errors.push(`Invalid category: ${metadata.category}`);
  }
  
  // Validate trial_days if trial_available
  if (metadata.trial_available === 'true' && !metadata.trial_days) {
    errors.push(`Missing trial_days when trial_available=true`);
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Get all unique categories from products
 * @param {Array} products - Array of normalized products
 * @returns {Array} Array of category objects
 */
export function getCategoriesFromProducts(products) {
  const categorySet = new Set();
  
  products.forEach(product => {
    if (product.marketplace && product.marketplace.categoryId) {
      categorySet.add(product.marketplace.categoryId);
    }
  });
  
  return Array.from(categorySet)
    .map(catId => CATEGORIES[catId] || CATEGORIES.other)
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}

/**
 * Get all unique providers from products
 * @param {Array} products - Array of normalized products
 * @returns {Array} Array of provider objects
 */
export function getProvidersFromProducts(products) {
  const providerSet = new Set();
  
  products.forEach(product => {
    if (product.marketplace && product.marketplace.providerId) {
      providerSet.add(product.marketplace.providerId);
    }
  });
  
  return Array.from(providerSet)
    .map(providerId => PROVIDERS[providerId])
    .filter(Boolean)
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}

/**
 * Sort products for marketplace display
 * @param {Array} products - Array of normalized products
 * @returns {Array} Sorted products
 */
export function sortProductsForMarketplace(products) {
  return [...products].sort((a, b) => {
    const aMarketplace = a.marketplace || {};
    const bMarketplace = b.marketplace || {};
    
    // Featured first
    if (aMarketplace.marketplaceFeatured && !bMarketplace.marketplaceFeatured) return -1;
    if (!aMarketplace.marketplaceFeatured && bMarketplace.marketplaceFeatured) return 1;
    
    // Then by priority (lower is better)
    if (aMarketplace.marketplacePriority !== bMarketplace.marketplacePriority) {
      return aMarketplace.marketplacePriority - bMarketplace.marketplacePriority;
    }
    
    // Finally alphabetically by name
    return (a.name || '').localeCompare(b.name || '');
  });
}

/**
 * Get all unique tags from products
 * @param {Array} products - Array of normalized products
 * @returns {Array} Array of unique tag strings, sorted
 */
export function getTagsFromProducts(products) {
  const tagSet = new Set();
  
  products.forEach(product => {
    const tags = product.marketplace?.tags || [];
    tags.forEach(tag => {
      if (tag && typeof tag === 'string' && tag.trim()) {
        tagSet.add(tag.trim().toLowerCase());
      }
    });
  });
  
  return Array.from(tagSet).sort();
}

/**
 * Filter products by tags
 * @param {Array} products - Array of normalized products
 * @param {Array} selectedTags - Array of selected tag strings
 * @returns {Array} Filtered products
 */
export function filterByTags(products, selectedTags) {
  if (!selectedTags || selectedTags.length === 0) {
    return products;
  }
  
  return products.filter(product => {
    const productTags = (product.marketplace?.tags || []).map(t => 
      typeof t === 'string' ? t.trim().toLowerCase() : ''
    );
    
    // Product must have at least one of the selected tags
    return selectedTags.some(selectedTag => 
      productTags.includes(selectedTag.trim().toLowerCase())
    );
  });
}

/**
 * Filter products by marketplace criteria
 * @param {Array} products - Array of normalized products
 * @param {Object} filters - Filter criteria
 * @param {string} filters.vendorType - 'all' | 'besper' | 'third_party'
 * @param {string} filters.providerId - Provider ID or 'all'
 * @param {string} filters.categoryId - Category ID or 'all'
 * @param {string} filters.searchQuery - Search query string
 * @param {Array} filters.selectedTags - Array of selected tag strings
 * @param {string} filters.productType - Product type filter
 * @param {string} filters.releaseStatus - Release status filter
 * @returns {Array} Filtered products
 */
export function filterMarketplaceProducts(products, filters) {
  let filtered = [...products];
  
  console.log('filterMarketplaceProducts called with:', {
    totalProducts: products.length,
    filters: filters,
    sampleProduct: products[0] ? {
      name: products[0].name,
      marketplace: products[0].marketplace
    } : null
  });

  if (filters.resourceType && filters.resourceType !== 'all') {
    filtered = filtered.filter((p) => {
      const rt = (p.metadata && p.metadata.resource_type) || '';
      return rt === filters.resourceType;
    });
  }
  
  // Filter by vendor type
  if (filters && filters.vendorType && filters.vendorType !== 'all') {
    const beforeCount = filtered.length;
    console.log(`🔍 Filtering by vendorType: "${filters.vendorType}" from ${beforeCount} products`);
    
    filtered = filtered.filter(p => {
      // Get vendorType from marketplace, default to 'besper' if not set
      const productVendorType = p.marketplace?.vendorType || 'besper';
      const matches = productVendorType === filters.vendorType;
      
      if (matches) {
        console.log(`  ✅ "${p.name}" - vendorType: "${productVendorType}" MATCHES filter "${filters.vendorType}"`);
      } else {
        console.log(`  ❌ "${p.name}" - vendorType: "${productVendorType}" does NOT match filter "${filters.vendorType}"`);
      }
      
      return matches;
    });
    
    console.log(`📊 Vendor filter result: ${filtered.length} of ${beforeCount} products remain (filter: "${filters.vendorType}")`);
  } else {
    console.log('ℹ️ No vendor filter applied (vendorType is "all" or missing)');
  }
  
  // Filter by provider
  if (filters.providerId && filters.providerId !== 'all') {
    filtered = filtered.filter(p => 
      p.marketplace?.providerId === filters.providerId
    );
  }
  
  // Filter by category
  if (filters.categoryId && filters.categoryId !== 'all') {
    filtered = filtered.filter(p => 
      p.marketplace?.categoryId === filters.categoryId
    );
  }
  
  // Filter by tags
  if (filters.selectedTags && filters.selectedTags.length > 0) {
    filtered = filterByTags(filtered, filters.selectedTags);
  }
  
  // Filter by product type
  if (filters.productType && filters.productType !== 'all') {
    filtered = filtered.filter(p => {
      const name = (p.name || '').toLowerCase();
      const id = (p.id || '').toLowerCase();
      const metadataProductType = (p.metadata?.product_type || p.marketplace?.productType || '').toLowerCase();
      
      if (filters.productType === 'implementation') {
        return name.includes('besper bot') || name.includes('b-esper bot') || id.includes('besper-bot') ||
               metadataProductType === 'implementation';
      }
      if (filters.productType === 'application') {
        const rt = (p.metadata?.resource_type || '').toLowerCase();
        return (name.includes('finance') && name.includes('besper')) ||
               metadataProductType === 'application' ||
               rt === 'ops' ||
               rt === 'besper_os' ||
               rt === 'key_vault' ||
               rt === 'dataset' ||
               rt === 'ai_lab';
      }
      if (filters.productType === 'partner') {
        return name.includes('resale') || id.includes('resale') ||
               metadataProductType === 'partner';
      }
      return true;
    });
  }
  
  // Filter by release status
  if (filters.releaseStatus && filters.releaseStatus !== 'all') {
    filtered = filtered.filter(p => {
      const releaseStatus = (p.metadata?.release_status || p.marketplace?.release_status || 'available').toLowerCase();
      return releaseStatus === filters.releaseStatus.toLowerCase();
    });
  }
  
  // Filter by search query
  if (filters.searchQuery && filters.searchQuery.trim()) {
    const query = filters.searchQuery.toLowerCase().trim();
    filtered = filtered.filter(p => {
      const name = (p.name || '').toLowerCase();
      const description = (p.description || '').toLowerCase();
      const shortDesc = (p.marketplace?.shortDescription || '').toLowerCase();
      const features = (p.marketplace?.features || []).join(' ').toLowerCase();
      const tags = (p.marketplace?.tags || []).join(' ').toLowerCase();
      const category = (p.marketplace?.category?.displayName || '').toLowerCase();
      const provider = (p.marketplace?.providerDisplayName || '').toLowerCase();
      
      return name.includes(query) ||
        description.includes(query) ||
        shortDesc.includes(query) ||
        features.includes(query) ||
        tags.includes(query) ||
        category.includes(query) ||
        provider.includes(query);
    });
  }
  
  return filtered;
}


